import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { usePageLoader } from '../context/PageLoaderContext';
import { savePendingAction } from '../utils/pendingAction';
import CachedImage from '../components/CachedImage';
import ComboCard from '../components/product/ComboCard';
import {
  getImagesForColor,
  getPrimaryImageForColor,
  getColorTaggedImage,
} from '../utils/productImagesByColor';
import './ProductDetail.css';
import './ComboDetail.css';
import '../components/product/ProductCard.css';

const parseFeatures = (features) => {
  if (!features) return [];
  if (Array.isArray(features)) return features;
  if (typeof features === 'string') {
    try {
      const parsed = JSON.parse(features);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const ProductDetailsContent = ({ product }) => {
  const featList = parseFeatures(product.features);
  const hasList = featList.length > 0;

  return (
    <>
      {(product.fit || product.product_type) && (
        <div className="detail-subsection">
          <h6>Fit</h6>
          <p>
            {product.fit ||
              `Designed with a comfortable regular fit, this ${(product.product_type || '').toLowerCase()} offers ease of movement without feeling loose.`}
          </p>
        </div>
      )}

      <div className="detail-subsection">
        <h6>Features</h6>
        <ul>
          {hasList ? (
            featList.map((item, i) => (
              <li key={i}>{typeof item === 'string' ? item : String(item)}</li>
            ))
          ) : (
            <>
              {product.fabric_type && <li>Soft, breathable {product.fabric_type.toLowerCase()} fabric</li>}
              {product.pattern && <li>Distinctive {product.pattern.toLowerCase()} pattern</li>}
              <li>Full button-front closure</li>
              {product.texture && <li>Unique {product.texture.toLowerCase()} texture</li>}
              <li>Lightweight inner finish for all-day comfort</li>
            </>
          )}
        </ul>
      </div>

      <div className="detail-subsection">
        <h6>Material And Care</h6>
        {product.fabric_type && (
          <p>
            <strong>Fabric:</strong> {product.fabric_type}
          </p>
        )}
        {(product.product_type || product.occasion || product.pattern || product.texture) && (
          <div className="detail-subsection">
            {product.product_type && <p><strong>Product type:</strong> {product.product_type}</p>}
            {product.occasion && <p><strong>Occasion:</strong> {product.occasion}</p>}
            {product.pattern && <p><strong>Pattern:</strong> {product.pattern}</p>}
            {product.texture && <p><strong>Texture:</strong> {product.texture}</p>}
          </div>
        )}
        {product.care_instructions && (
          <p><strong>Care Instructions:</strong> {product.care_instructions}</p>
        )}
        {product.safety_instructions && (
          <p><strong>Safety Instructions:</strong> {product.safety_instructions}</p>
        )}
      </div>
    </>
  );
};

const ComboDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { setPageLoading } = usePageLoader();
  const detailsRef = useRef(null);

  const [combo, setCombo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selections, setSelections] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [moreCombos, setMoreCombos] = useState([]);
  const [showDelivery, setShowDelivery] = useState(false);
  const [expandedProductId, setExpandedProductId] = useState(null);
  const [sortBy, setSortBy] = useState('newest');
  const [showSizeChartModal, setShowSizeChartModal] = useState(false);
  const [sizeChart, setSizeChart] = useState(null);
  const [loadingSizeChart, setLoadingSizeChart] = useState(false);

  useEffect(() => {
    setPageLoading(loading);
    return () => setPageLoading(false);
  }, [loading, setPageLoading]);

  useEffect(() => {
    fetchCombo();
  }, [id]);

  const fetchCombo = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/combos/${id}`);
      setCombo(response);

      const initialSelections = (response.products || []).map((product) => {
        const firstColorVariant = product.variants?.find((v) => v.stock_quantity > 0);
        const colorId = firstColorVariant?.color_id;
        const sizesForColor = product.variants
          ?.filter((v) => v.color_id === colorId && v.stock_quantity > 0)
          .map((v) => v.size);
        const firstSize = sizesForColor?.[0] || null;
        const variant = product.variants?.find(
          (v) => v.color_id === colorId && v.size === firstSize
        );

        return {
          product_id: product.id,
          label: product.label,
          color_id: colorId || null,
          color_name: firstColorVariant?.color_name || '',
          size: firstSize,
          variant_id: variant?.id || null,
        };
      });
      setSelections(initialSelections);

      const productIds = (response.products || []).map((p) => p.id);
      const reviewPromises = productIds.map((pid) =>
        api.get(`/reviews/product/${pid}`).catch(() => ({ reviews: [], stats: {} }))
      );
      const [moreCombosRes, ...reviewResults] = await Promise.all([
        api.get('/combos?is_active=true&limit=8'),
        ...reviewPromises,
      ]);

      const allReviews = [];
      reviewResults.forEach((result) => {
        (result.reviews || []).forEach((review) => {
          allReviews.push(review);
        });
      });
      setReviews(allReviews);

      setMoreCombos((moreCombosRes.combos || []).filter((c) => String(c.id) !== String(id)));
    } catch (error) {
      toast.error('Combo not found');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const displayImages = useMemo(() => {
    if (!combo) return [];
    const images = [];
    const pushUnique = (img) => {
      if (img?.image_url && !images.find((i) => i.image_url === img.image_url)) {
        images.push(img);
      }
    };

    // Product 1 + Product 2 selected (or default first) variant images only — no combo thumbnail
    (combo.products || []).slice(0, 2).forEach((product, idx) => {
      const colorId = selections[idx]?.color_id;
      const colorImages = getImagesForColor(product.images, colorId);
      if (colorImages.length > 0) {
        colorImages.forEach((img, imgIdx) => {
          pushUnique({
            id: `product-${idx}-${img.id || imgIdx}`,
            image_url: img.image_url,
          });
        });
      } else {
        const url = getPrimaryImageForColor(
          product.images,
          colorId,
          product.primary_image || product.images?.[0]?.image_url
        );
        pushUnique({ id: `product-${idx}`, image_url: url });
      }
    });

    return images.length > 0 ? images : [{ id: 'placeholder', image_url: '/placeholder.png' }];
  }, [combo, selections]);

  const selectionColorKey = selections.map((s) => s.color_id).join(',');

  useEffect(() => {
    setSelectedImageIndex(0);
  }, [selectionColorKey]);

  const updateSelection = (productIndex, updates) => {
    setSelections((prev) => {
      const next = [...prev];
      next[productIndex] = { ...next[productIndex], ...updates };
      return next;
    });
  };

  const handleColorSelect = (productIndex, colorId, colorName) => {
    const product = combo.products[productIndex];
    const sizesForColor = product.variants
      ?.filter((v) => v.color_id === colorId && v.stock_quantity > 0)
      .map((v) => v.size)
      .filter((s, i, self) => self.indexOf(s) === i);
    const firstSize = sizesForColor?.[0] || null;
    const variant = product.variants?.find((v) => v.color_id === colorId && v.size === firstSize);

    updateSelection(productIndex, {
      color_id: colorId,
      color_name: colorName,
      size: firstSize,
      variant_id: variant?.id || null,
    });
  };

  const handleSizeSelect = (productIndex, size) => {
    const product = combo.products[productIndex];
    const sel = selections[productIndex];
    const variant = product.variants?.find((v) => v.color_id === sel.color_id && v.size === size);
    updateSelection(productIndex, { size, variant_id: variant?.id || null });
  };

  const fetchSizeChart = async (sizeChartId) => {
    try {
      setLoadingSizeChart(true);
      const response = await api.get(`/size-charts/${sizeChartId}`);
      setSizeChart(response);
      return response;
    } catch (error) {
      console.error('Error fetching size chart:', error);
      setSizeChart(null);
      return null;
    } finally {
      setLoadingSizeChart(false);
    }
  };

  const handleSizeGuideClick = async (e, product) => {
    e.preventDefault();
    if (!product?.size_chart_id) {
      toast.info('Size guide not available for this product');
      return;
    }
    if (sizeChart?.id === product.size_chart_id) {
      setShowSizeChartModal(true);
      return;
    }
    const chart = await fetchSizeChart(product.size_chart_id);
    if (chart) {
      setShowSizeChartModal(true);
    } else {
      toast.info('Size guide not available for this product');
    }
  };

  const allSelectionsValid = selections.length === 2 && selections.every((s) => s.variant_id);

  const hasInactiveProduct = useMemo(() => {
    return (combo?.products || []).some((p) => !p.is_active || p.is_active === 0 || p.is_active === '0');
  }, [combo]);

  const hasProductFullyOutOfStock = useMemo(() => {
    return (combo?.products || []).some((product) => {
      const variants = product.variants || [];
      if (variants.length === 0) return true;
      return !variants.some((v) => (parseInt(v.stock_quantity, 10) || 0) > 0);
    });
  }, [combo]);

  const minStock = useMemo(() => {
    if (!allSelectionsValid) return 0;
    let min = Infinity;
    for (const sel of selections) {
      const product = combo?.products?.find((p) => p.id === sel.product_id);
      if (!product?.is_active || product.is_active === 0 || product.is_active === '0') {
        return 0;
      }
      const variant = product?.variants?.find((v) => v.id === sel.variant_id);
      const stock = parseInt(variant?.stock_quantity, 10) || 0;
      if (stock < min) min = stock;
    }
    return min === Infinity ? 0 : min;
  }, [selections, combo, allSelectionsValid]);

  const canAddToCart =
    allSelectionsValid &&
    minStock > 0 &&
    !hasInactiveProduct &&
    !hasProductFullyOutOfStock &&
    !!combo?.is_active;

  const unavailableMessage = !combo?.is_active
    ? 'This combo is currently unavailable'
    : hasInactiveProduct
      ? 'One or more products in this combo are inactive'
      : hasProductFullyOutOfStock
        ? 'One or more products in this combo are out of stock'
        : !allSelectionsValid
          ? 'Please select color and size for both products'
          : minStock === 0
            ? 'Selected combination is out of stock'
            : null;

  const separateTotal = useMemo(() => {
    if (!combo?.products) return 0;
    return combo.products.reduce((sum, p) => sum + (parseFloat(p.price) || 0), 0);
  }, [combo]);

  const handleAddToCart = async () => {
    if (!canAddToCart) {
      toast.error(unavailableMessage || 'Cannot add this combo to cart');
      return;
    }
    if (quantity > minStock) {
      toast.error(`Only ${minStock} combo${minStock === 1 ? '' : 's'} available in stock`);
      return;
    }

    const comboSelections = selections.map((s) => ({
      product_id: s.product_id,
      variant_id: s.variant_id,
      label: s.label,
    }));

    if (!isAuthenticated) {
      savePendingAction({ type: 'addComboToCart', comboId: id, comboSelections, quantity });
      toast.info('Please log in to add items to your cart');
      setTimeout(() => navigate('/login'), 400);
      return;
    }

    try {
      await api.post('/cart', {
        combo_id: parseInt(id, 10),
        combo_selections: comboSelections,
        quantity,
      });
      toast.success('Combo added to cart');
      window.dispatchEvent(new Event('cartUpdated'));
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to add combo to cart');
    }
  };

  const getProductImage = (product, sel) =>
    getPrimaryImageForColor(
      product.images,
      sel?.color_id,
      product.primary_image || '/placeholder.png'
    );

  const getProductDiscount = (product) => {
    const price = parseFloat(product.price) || 0;
    const strike = parseFloat(product.strike_through_price) || 0;
    if (strike <= price) return 0;
    return Math.round(((strike - price) / strike) * 100);
  };

  const sortedReviews = useMemo(() => {
    const list = [...reviews];
    if (sortBy === 'newest') list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    else if (sortBy === 'oldest') list.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    else if (sortBy === 'highest') list.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    else if (sortBy === 'lowest') list.sort((a, b) => (a.rating || 0) - (b.rating || 0));
    return list;
  }, [reviews, sortBy]);

  const getTimeAgo = (date) => {
    const now = new Date();
    const reviewDate = new Date(date);
    const diffInSeconds = Math.floor((now - reviewDate) / 1000);
    if (diffInSeconds < 60) return 'Just now';
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return `${diffInWeeks} ${diffInWeeks === 1 ? 'week' : 'weeks'} ago`;
    const diffInMonths = Math.floor(diffInDays / 30);
    return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`;
  };

  if (loading || !combo) return null;

  const comboPrice = parseFloat(combo.price) || 0;
  const strikePrice = parseFloat(combo.strike_through_price) || 0;
  const savings = Math.max(0, separateTotal - comboPrice);
  const referencePrice = strikePrice > comboPrice ? strikePrice : separateTotal;
  const comboDiscountPercent =
    referencePrice > comboPrice ? Math.round(((referencePrice - comboPrice) / referencePrice) * 100) : 0;
  const selectionsSummary = (combo.products || [])
    .map((product, index) => {
      const sel = selections[index] || {};
      const sizePart = sel.size ? ` (${sel.size})` : '';
      return `${product.name}${sizePart}`;
    })
    .join(' • ');
  const mainImage = displayImages[selectedImageIndex]?.image_url || displayImages[0]?.image_url || '/placeholder.png';

  const anyReturnable = combo.products?.some(
    (p) => p.returnable === true || p.returnable === 1
  );
  const anyReplacement = combo.products?.some(
    (p) => p.replacement_allowed === true || p.replacement_allowed === 1
  );

  return (
    <Container className="py-4 product-detail-page combo-detail-page">
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/products" className="text-decoration-none">Products</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">{combo.title}</li>
        </ol>
      </nav>

      <Row>
        <Col md={5}>
          <div className="product-images">
            <div className="main-image-wrapper">
              <CachedImage src={mainImage} alt={combo.title} className="main-product-image" />
            </div>
            {displayImages.length > 1 && (
              <div className="thumbnail-images">
                {displayImages.map((img, idx) => (
                  <div
                    key={img.id ?? idx}
                    className={`thumbnail-item ${selectedImageIndex === idx ? 'active' : ''}`}
                    onClick={() => setSelectedImageIndex(idx)}
                  >
                    <CachedImage src={img.image_url} alt={`${combo.title} ${idx + 1}`} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </Col>

        <Col md={7}>
          <div className="combo-badge">COMBO</div>
          <h1 className="product-detail-title">{combo.title}</h1>
          {combo.description && <p className="product-detail-subtitle mb-3">{combo.description}</p>}

          <div className="product-price-section mb-4" style={{ borderBottom: '1px solid #e0e0e0', paddingBottom: '16px' }}>
            <div className="d-flex align-items-baseline gap-3 flex-wrap">
              <span className="current-price-large">₹{comboPrice.toLocaleString('en-IN')}</span>
              {strikePrice > 0 && (
                <span className="original-price-large" style={{ textDecoration: 'line-through', color: '#999' }}>
                  ₹{strikePrice.toLocaleString('en-IN')}
                </span>
              )}
              {combo.discount_percent > 0 && <span className="discount-badge">-{combo.discount_percent}%</span>}
            </div>
            {savings > 0 && (
              <div className="combo-savings">You save ₹{savings.toLocaleString('en-IN')} on this combo</div>
            )}
          </div>

          {combo.products?.map((product, productIndex) => {
            const sel = selections[productIndex] || {};
            const uniqueColors = product.variants?.filter(
              (v, idx, self) => self.findIndex((x) => x.color_id === v.color_id) === idx
            ) || [];
            const availableSizes = product.variants
              ?.filter((v) => !sel.color_id || v.color_id === sel.color_id)
              .map((v) => v.size)
              .filter((s, i, self) => self.indexOf(s) === i) || [];

            return (
              <div key={product.id} className="product-selection-section">
                <h3 className="product-selection-title">
                  {productIndex + 1}. Choose {product.name}
                </h3>

                {uniqueColors.length > 0 && (
                  <div className="mb-4">
                    <label className="variation-label">Variations: {sel.color_name || 'Select'}</label>
                    <div className="color-swatches">
                      {uniqueColors.map((variant) => {
                        const colorVariants = product.variants.filter((v) => v.color_id === variant.color_id);
                        const hasStock = colorVariants.some((v) => v.stock_quantity > 0);
                        const isSelected = sel.color_id === variant.color_id;
                        const swatchImage = getColorTaggedImage(product.images, variant.color_id);
                        return (
                          <button
                            key={variant.color_id}
                            type="button"
                            className={`color-swatch ${swatchImage ? 'color-swatch-image' : ''} ${isSelected ? 'active' : ''} ${!hasStock ? 'unavailable' : ''}`}
                            style={
                              swatchImage
                                ? undefined
                                : { backgroundColor: variant.color_hex || '#ccc' }
                            }
                            onClick={() => hasStock && handleColorSelect(productIndex, variant.color_id, variant.color_name)}
                            disabled={!hasStock}
                            title={hasStock ? variant.color_name : `${variant.color_name} - Out of Stock`}
                          >
                            {swatchImage && (
                              <CachedImage
                                src={swatchImage}
                                alt={variant.color_name || 'Color'}
                                className="color-swatch-img"
                              />
                            )}
                            {!hasStock && (
                              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
                                <path d="M-1 0.000183105L48.3426 49.3428" stroke="#E9E9E9" strokeWidth="1.2" strokeLinecap="round" strokeDasharray="6 6" />
                              </svg>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {availableSizes.length > 0 && (
                  <div className="mb-4">
                    <label className="size-label">
                      Size:{' '}
                      {product?.size_chart_id && (
                        <Link
                          to="#"
                          className="size-guide-link"
                          onClick={(e) => handleSizeGuideClick(e, product)}
                        >
                          Size Guide
                        </Link>
                      )}
                    </label>
                    <div className="size-buttons">
                      {availableSizes.map((size) => {
                        const variant = product.variants.find(
                          (v) => v.color_id === sel.color_id && v.size === size
                        );
                        const inStock = variant && variant.stock_quantity > 0;
                        const isSelected = sel.size === size;
                        return (
                          <button
                            key={size}
                            type="button"
                            className={`size-button ${isSelected ? 'selected' : ''} ${!inStock ? 'out-of-stock' : ''}`}
                            disabled={!inStock}
                            onClick={() => handleSizeSelect(productIndex, size)}
                            style={{ position: 'relative' }}
                          >
                            {size}
                            {!inStock && (
                              <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50" fill="none" style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
                                <path d="M5 5L45 45" stroke="#999" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="4 4" />
                              </svg>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <div className="quantity-selector mb-4 pb-4" style={{ borderBottom: '1px solid #e0e0e0' }}>
            <label className="quantity-label">Quantity:</label>
            <div className="quantity-controls">
              <button type="button" className="quantity-btn" onClick={() => setQuantity(Math.max(1, quantity - 1))}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
              </button>
              <input type="number" value={quantity} onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))} min={1} max={minStock || 10} className="quantity-input" />
              <button type="button" className="quantity-btn" onClick={() => {
                const maxStock = minStock || 10;
                if (quantity >= maxStock) { toast.info(`Only ${maxStock} item${maxStock === 1 ? '' : 's'} left`); return; }
                setQuantity(quantity + 1);
              }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
              </button>
            </div>
          </div>

          <Button className="add-to-cart-btn" onClick={handleAddToCart} disabled={!canAddToCart}>
            ADD COMBO TO CART
          </Button>

          {unavailableMessage && (
            <div className="text-danger small mb-3">{unavailableMessage}</div>
          )}

          <div className="shipping-info mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
              <path d="M8.66732 25.3333H6.00065C5.63265 25.3333 5.33398 25.0347 5.33398 24.6667C5.33398 24.2987 5.63265 24 6.00065 24H8.66732C9.03532 24 9.33398 24.2987 9.33398 24.6667C9.33398 25.0347 9.03532 25.3333 8.66732 25.3333Z" fill="#1F1F1F" />
              <path d="M30.3321 25.334H28.6654C28.2974 25.334 27.9988 25.0353 27.9988 24.6673C27.9988 24.2993 28.2974 24.0007 28.6654 24.0007H29.7788L30.6774 19.2113C30.6654 16.7607 28.5721 14.6673 25.9988 14.6673H21.6214L19.5001 24.0007H23.3321C23.7001 24.0007 23.9988 24.2993 23.9988 24.6673C23.9988 25.0353 23.7001 25.334 23.3321 25.334H18.6654C18.4628 25.334 18.2708 25.242 18.1441 25.0833C18.0174 24.926 17.9694 24.718 18.0148 24.5207L20.4388 13.854C20.5081 13.5487 20.7774 13.334 21.0894 13.334H25.9988C29.3068 13.334 31.9988 16.026 31.9988 19.334L30.9868 24.79C30.9281 25.106 30.6534 25.334 30.3321 25.334Z" fill="#1F1F1F" />
              <path d="M25.9993 28.0006C24.162 28.0006 22.666 26.506 22.666 24.6673C22.666 22.8286 24.162 21.334 25.9993 21.334C27.8367 21.334 29.3327 22.8286 29.3327 24.6673C29.3327 26.506 27.8367 28.0006 25.9993 28.0006ZM25.9993 22.6673C24.8967 22.6673 23.9993 23.5646 23.9993 24.6673C23.9993 25.77 24.8967 26.6673 25.9993 26.6673C27.102 26.6673 27.9993 25.77 27.9993 24.6673C27.9993 23.5646 27.102 22.6673 25.9993 22.6673Z" fill="#1F1F1F" />
              <path d="M11.3333 28.0006C9.496 28.0006 8 26.506 8 24.6673C8 22.8286 9.496 21.334 11.3333 21.334C13.1707 21.334 14.6667 22.8286 14.6667 24.6673C14.6667 26.506 13.1707 28.0006 11.3333 28.0006ZM11.3333 22.6673C10.2307 22.6673 9.33333 23.5646 9.33333 24.6673C9.33333 25.77 10.2307 26.6673 11.3333 26.6673C12.436 26.6673 13.3333 25.77 13.3333 24.6673C13.3333 23.5646 12.436 22.6673 11.3333 22.6673Z" fill="#1F1F1F" />
              <path d="M8.66602 13.3333H3.33268C2.96468 13.3333 2.66602 13.0347 2.66602 12.6667C2.66602 12.2987 2.96468 12 3.33268 12H8.66602C9.03402 12 9.33268 12.2987 9.33268 12.6667C9.33268 13.0347 9.03402 13.3333 8.66602 13.3333Z" fill="#1F1F1F" />
              <path d="M8.66732 17.3333H2.00065C1.63265 17.3333 1.33398 17.0347 1.33398 16.6667C1.33398 16.2987 1.63265 16 2.00065 16H8.66732C9.03532 16 9.33398 16.2987 9.33398 16.6667C9.33398 17.0347 9.03532 17.3333 8.66732 17.3333Z" fill="#1F1F1F" />
              <path d="M8.66667 21.3333H0.666667C0.298667 21.3333 0 21.0347 0 20.6667C0 20.2987 0.298667 20 0.666667 20H8.66667C9.03467 20 9.33333 20.2987 9.33333 20.6667C9.33333 21.0347 9.03467 21.3333 8.66667 21.3333Z" fill="#1F1F1F" />
              <path d="M18.6673 25.334H14.0007C13.6327 25.334 13.334 25.0353 13.334 24.6673C13.334 24.2993 13.6327 24.0007 14.0007 24.0007H18.1353L21.1647 10.6673H6.00065C5.63265 10.6673 5.33398 10.3687 5.33398 10.0007C5.33398 9.63265 5.63265 9.33398 6.00065 9.33398H22.0007C22.2033 9.33398 22.3953 9.42598 22.522 9.58465C22.6487 9.74198 22.6967 9.94998 22.6513 10.1473L19.318 24.814C19.2487 25.1193 18.978 25.334 18.6673 25.334Z" fill="#1F1F1F" />
            </svg>
            {canAddToCart ? (
              <span className="stock-status">In Stock - Available For Immediate Shipping</span>
            ) : (
              <span className="stock-status out-of-stock-message">{unavailableMessage || 'Out of Stock - Please select different options'}</span>
            )}
          </div>
        </Col>
      </Row>

      {/* What's in this Combo */}
      <section className="combo-whats-included mt-5">
        <div className="combo-whats-included-header text-center mb-4">
          <h2 className="section-title mb-2">What&apos;s in this Combo?</h2>
          <p className="combo-whats-included-subtitle">Handpicked pieces that go perfectly together.</p>
        </div>

        <Row className="g-4 justify-content-center">
          {combo.products?.map((product, productIndex) => {
            const sel = selections[productIndex] || {};
            const price = parseFloat(product.price) || 0;
            const strike = parseFloat(product.strike_through_price) || 0;
            const discount = getProductDiscount(product);
            const variantText = [sel.color_name, sel.size].filter(Boolean).join(' • ');

            return (
              <Col key={product.id} md={6} lg={5}>
                <div className="combo-included-card">
                  <CachedImage src={getProductImage(product, sel)} alt={product.name} className="combo-included-image" />
                  <div className="combo-included-info">
                    <h4 className="combo-included-name">{product.name}</h4>
                    {variantText && <p className="combo-included-variant">{variantText}</p>}
                    <div className="combo-included-pricing">
                      <span className="combo-included-price">₹{price.toLocaleString('en-IN')}</span>
                      {strike > price && (
                        <>
                          <span className="combo-included-strike">₹{strike.toLocaleString('en-IN')}</span>
                          {discount > 0 && <span className="combo-included-discount">({discount}% OFF)</span>}
                        </>
                      )}
                    </div>
                    <Link to={`/products/${product.id}`} className="view-product-btn combo-included-view-btn">
                      View Product
                    </Link>
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>

        {savings > 0 && (
          <div className="combo-savings-banner">
            <div className="combo-savings-banner-left">
              <span className="combo-savings-banner-icon" aria-hidden="true">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M20.59 13.41L13.42 20.58C13.04 20.96 12.53 21.17 12 21.17C11.47 21.17 10.96 20.96 10.58 20.58L3.41 13.41C2.63 12.63 2.63 11.37 3.41 10.59L10.58 3.42C11.37 2.63 12.63 2.63 13.41 3.42L20.58 10.59C21.37 11.37 21.37 12.63 20.59 13.41Z" stroke="#E67E22" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="12" cy="12" r="1.5" fill="#E67E22" />
                </svg>
              </span>
              <div>
                <strong>You&apos;re saving ₹{savings.toLocaleString('en-IN')} on this combo</strong>
                {separateTotal > 0 && (
                  <p className="mb-0">Buy products separately for ₹{separateTotal.toLocaleString('en-IN')}</p>
                )}
              </div>
            </div>
            <button type="button" className="combo-savings-view-btn" onClick={() => detailsRef.current?.scrollIntoView({ behavior: 'smooth' })}>
              View Details
            </button>
          </div>
        )}

        <div className="combo-summary-bar">
          <div className="combo-summary-left">
            <CachedImage
              src={mainImage}
              alt={combo.title}
              className="combo-summary-thumb"
            />
            <div className="combo-summary-meta">
              <h3 className="combo-summary-title">{combo.title}</h3>
              {selectionsSummary && <p className="combo-summary-variants">{selectionsSummary}</p>}
              {savings > 0 && (
                <p className="combo-summary-save">You save ₹{savings.toLocaleString('en-IN')}</p>
              )}
            </div>
          </div>

          <div className="combo-summary-pricing">
            <span className="combo-summary-price-label">Total Price</span>
            <div className="combo-summary-price-row">
              <span className="combo-summary-price">₹{comboPrice.toLocaleString('en-IN')}</span>
              {referencePrice > comboPrice && (
                <>
                  <span className="combo-summary-strike">₹{referencePrice.toLocaleString('en-IN')}</span>
                  {comboDiscountPercent > 0 && (
                    <span className="combo-summary-discount">({comboDiscountPercent}% OFF)</span>
                  )}
                </>
              )}
            </div>
          </div>

          <Button
            className="combo-summary-cart-btn"
            onClick={handleAddToCart}
            disabled={!canAddToCart}
          >
            Add Combo to Cart
            <svg width="20" height="20" viewBox="0 0 32 32" fill="none" aria-hidden="true">
              <path d="M8.66732 25.3333H6.00065C5.63265 25.3333 5.33398 25.0347 5.33398 24.6667C5.33398 24.2987 5.63265 24 6.00065 24H8.66732C9.03532 24 9.33398 24.2987 9.33398 24.6667C9.33398 25.0347 9.03532 25.3333 8.66732 25.3333Z" fill="currentColor" />
              <path d="M30.3321 25.334H28.6654C28.2974 25.334 27.9988 25.0353 27.9988 24.6673C27.9988 24.2993 28.2974 24.0007 28.6654 24.0007H29.7788L30.6774 19.2113C30.6654 16.7607 28.5721 14.6673 25.9988 14.6673H21.6214L19.5001 24.0007H23.3321C23.7001 24.0007 23.9988 24.2993 23.9988 24.6673C23.9988 25.0353 23.7001 25.334 23.3321 25.334H18.6654C18.4628 25.334 18.2708 25.242 18.1441 25.0833C18.0174 24.926 17.9694 24.718 18.0148 24.5207L20.4388 13.854C20.5081 13.5487 20.7774 13.334 21.0894 13.334H25.9988C29.3068 13.334 31.9988 16.026 31.9988 19.334L30.9868 24.79C30.9281 25.106 30.6534 25.334 30.3321 25.334Z" fill="currentColor" />
              <path d="M25.9993 28.0006C24.162 28.0006 22.666 26.506 22.666 24.6673C22.666 22.8286 24.162 21.334 25.9993 21.334C27.8367 21.334 29.3327 22.8286 29.3327 24.6673C29.3327 26.506 27.8367 28.0006 25.9993 28.0006ZM25.9993 22.6673C24.8967 22.6673 23.9993 23.5646 23.9993 24.6673C23.9993 25.77 24.8967 26.6673 25.9993 26.6673C27.102 26.6673 27.9993 25.77 27.9993 24.6673C27.9993 23.5646 27.102 22.6673 25.9993 22.6673Z" fill="currentColor" />
              <path d="M11.3333 28.0006C9.496 28.0006 8 26.506 8 24.6673C8 22.8286 9.496 21.334 11.3333 21.334C13.1707 21.334 14.6667 22.8286 14.6667 24.6673C14.6667 26.506 13.1707 28.0006 11.3333 28.0006ZM11.3333 22.6673C10.2307 22.6673 9.33333 23.5646 9.33333 24.6673C9.33333 25.77 10.2307 26.6673 11.3333 26.6673C12.436 26.6673 13.3333 25.77 13.3333 24.6673C13.3333 23.5646 12.436 22.6673 11.3333 22.6673Z" fill="currentColor" />
              <path d="M8.66602 13.3333H3.33268C2.96468 13.3333 2.66602 13.0347 2.66602 12.6667C2.66602 12.2987 2.96468 12 3.33268 12H8.66602C9.03402 12 9.33268 12.2987 9.33268 12.6667C9.33268 13.0347 9.03402 13.3333 8.66602 13.3333Z" fill="currentColor" />
              <path d="M8.66732 17.3333H2.00065C1.63265 17.3333 1.33398 17.0347 1.33398 16.6667C1.33398 16.2987 1.63265 16 2.00065 16H8.66732C9.03532 16 9.33398 16.2987 9.33398 16.6667C9.33398 17.0347 9.03532 17.3333 8.66732 17.3333Z" fill="currentColor" />
              <path d="M8.66667 21.3333H0.666667C0.298667 21.3333 0 21.0347 0 20.6667C0 20.2987 0.298667 20 0.666667 20H8.66667C9.03467 20 9.33333 20.2987 9.33333 20.6667C9.33333 21.0347 9.03467 21.3333 8.66667 21.3333Z" fill="currentColor" />
              <path d="M18.6673 25.334H14.0007C13.6327 25.334 13.334 25.0353 13.334 24.6673C13.334 24.2993 13.6327 24.0007 14.0007 24.0007H18.1353L21.1647 10.6673H6.00065C5.63265 10.6673 5.33398 10.3687 5.33398 10.0007C5.33398 9.63265 5.63265 9.33398 6.00065 9.33398H22.0007C22.2033 9.33398 22.3953 9.42598 22.522 9.58465C22.6487 9.74198 22.6967 9.94998 22.6513 10.1473L19.318 24.814C19.2487 25.1193 18.978 25.334 18.6673 25.334Z" fill="currentColor" />
            </svg>
          </Button>
        </div>
      </section>

      {/* Product details, delivery, reviews — same structure as product detail page */}
      <div ref={detailsRef} className="combo-details-below mt-4">
        {combo.products?.map((product) => (
          <div key={product.id} className="product-details-section">
            <button
              type="button"
              className="details-toggle-btn"
              onClick={() => setExpandedProductId(expandedProductId === product.id ? null : product.id)}
            >
              <span>Product Details And Care — {product.name}</span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className={expandedProductId === product.id ? 'rotated' : ''}>
                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {expandedProductId === product.id && (
              <div className="details-content">
                <ProductDetailsContent product={product} />
              </div>
            )}
          </div>
        ))}

        <div className="delivery-section">
          <button type="button" className="section-toggle-btn" onClick={() => setShowDelivery(!showDelivery)}>
            <span>Delivery</span>
            <div className="delivery-header-right">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className={showDelivery ? 'rotated' : ''}>
                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </button>
          {showDelivery && (
            <div className="delivery-content">
              <p>Orders within Tamil Nadu will be delivered within 2 working days.</p>
              <p>Orders outside Tamil Nadu will be delivered within 3 - 4 working days.</p>
            </div>
          )}
        </div>

        <div className="return-replacement-section mt-3 mb-3 p-3 border rounded" style={{ borderColor: '#e0e0e0' }}>
          <h6 className="mb-2" style={{ fontWeight: 600, color: '#333' }}>Return & Replacement</h6>
          {anyReturnable || anyReplacement ? (
            <div className="d-flex flex-wrap gap-2">
              {anyReturnable && <span className="badge bg-success" style={{ fontSize: '0.85rem' }}>Returnable</span>}
              {anyReplacement && <span className="badge border text-dark" style={{ fontSize: '0.85rem' }}>Replacement allowed</span>}
            </div>
          ) : (
            <p className="text-muted mb-0 small">Return policy varies by item in this combo. See individual product pages for details.</p>
          )}
        </div>

        <div className="comments-section">
          <div className="comments-header">
            <h3 className="section-title">Comments</h3>
            <div className="sort-dropdown">
              <label>Sort By:</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
              </select>
            </div>
          </div>

          <div className="comments-list">
            {sortedReviews.length > 0 ? (
              sortedReviews.map((review) => (
                <div key={review.id} className="comment-item">
                  <div className="comment-header">
                    <div className="comment-user">
                      <div className="user-avatar" />
                      <div>
                        <div className="user-name">{review.user_name || 'Anonymous'}</div>
                        <div className="comment-meta">
                          <span className="comment-date">{getTimeAgo(review.created_at)}</span>
                          {(review.color_name || review.size) && (
                            <span className="comment-variant">
                              {[review.color_name, review.size].filter(Boolean).join(' / ')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="comment-text">{review.review_text || 'No comment provided.'}</div>
                </div>
              ))
            ) : (
              <p className="no-comments">No comments yet. Be the first to comment!</p>
            )}
          </div>
        </div>
      </div>

      {moreCombos.length > 0 && (
        <div className="similar-products-section mt-5">
          <h2 className="section-title mb-4">Explore More Combos</h2>
          <Row>
            {moreCombos.slice(0, 4).map((c) => (
              <Col key={c.id} md={4} sm={6} xs={6} lg={3} className="mb-4">
                <ComboCard combo={c} />
              </Col>
            ))}
          </Row>
          <div className="w-100 flex align-items-center justify-content-center">
            <Link to="/products?filter=combo" className="load-more-btn my-4 text-decoration-none">View All Combos</Link>
          </div>
        </div>
      )}

      {showSizeChartModal && (
        <div className="review-modal-overlay" onClick={() => setShowSizeChartModal(false)}>
          <div className="review-modal size-chart-modal" onClick={(e) => e.stopPropagation()}>
            <div className="review-modal-header">
              <h3>{sizeChart?.name || 'Size Guide'}</h3>
              <button type="button" onClick={() => setShowSizeChartModal(false)}>×</button>
            </div>
            <div className="review-modal-body">
              {loadingSizeChart ? (
                <div className="text-center py-4">
                  <p>Loading size guide...</p>
                </div>
              ) : sizeChart && sizeChart.chart_data ? (
                <div className="size-chart-modal-content">
                  {(() => {
                    let chartData = sizeChart.chart_data;
                    if (typeof chartData === 'string') {
                      try {
                        chartData = JSON.parse(chartData);
                      } catch {
                        return <p>Size chart data is not available.</p>;
                      }
                    }
                    if (!chartData.headers || !chartData.rows) {
                      return <p>Size chart data is not available.</p>;
                    }
                    return (
                      <div className="table-responsive">
                        <table className="table table-bordered size-chart-table">
                          <thead>
                            <tr>
                              {chartData.headers.map((header, index) => (
                                <th key={index} className="text-center fw-bold">{header}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {chartData.rows.map((row, rowIndex) => (
                              <tr key={rowIndex}>
                                {row.map((cell, colIndex) => (
                                  <td key={colIndex} className="text-center">{cell}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <p>Size guide is not available for this product.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};

export default ComboDetail;
